<!-- eslint-disable vue/no-v-html -->
<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
  <ContentSection>
    <div
      v-if="hasNav"
      class="sticky top-navbar-height z-10 border-b-2 border-grey-100 bg-white max-md:hidden"
    >
      <Container tag="div" class="flex flex-wrap gap-12 font-semibold typo-subtitle">
        <NuxtLink
          v-for="step, index in data.steps"
          :key="step.id"
          :to="`#${step.id}`"
          class="pb-6"
          :class="{ 'text-primary-900': activeStep === index }"
        >
          {{ step.title }}
        </NuxtLink>
      </Container>
    </div>
    <AnimatedContainer>
      <Container tag="div" class="mt-18">
        <div
          v-for="step, index in data.steps"
          :id="step.id"
          :key="step.id"
          :ref="(el) => {
            steps[index] = el as HTMLElement
          }"
          class="
            slide-up
            max-md:flex-col max-md:border-l-2 max-md:border-primary-900 max-md:pl-3
            md:flex md:gap-16
          "
          :class="{ 'max-md:mb-12 md:pb-6': index !== data.steps.length - 1 }"
        >
          <div
            class="
              md:flex md:flex-col md:items-center md:after:mt-6 md:after:block md:after:h-full md:after:w-1
              md:after:bg-primary-900
            "
          >
            <div
              class="
                flex size-10 items-center justify-center rounded-100 bg-primary-900 py-3 font-semibold
                text-white typo-subtitle-sm
                max-md:mb-4
                md:size-12 md:typo-subtitle
              "
            >
              {{ (index + 1).toString().padStart(2, '0') }}
            </div>
          </div>
          <div
            :class="index !== data.steps.length - 1 ? 'md:pb-16'
              : !data.pdf?.permalink ? 'md:pb-2' : `pb-22`"
          >
            <h3 class="mb-4 font-semibold typo-lead md:mb-6">
              {{ step.title }}
            </h3>
            <div class="max-w-text typo-body wysiwyg process-content" v-html="step.description" />
          </div>
        </div>
        <AppButton
          v-if="data.pdf?.permalink"
          class="-mt-12 ml-4 md:-mt-5 md:ml-28"
          :download="getFilename(data.pdf.path)"
          :to="data.pdf.permalink"
        >
          {{ $t('download_method_as_pdf') }}
        </AppButton>
      </Container>
    </AnimatedContainer>
  </ContentSection>
</template>

<script setup lang="ts">
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

import type { MainComponentsProcessFragment } from '#gql'

gsap.registerPlugin(ScrollTrigger)

const props = defineProps<{
  data: Enforce<MainComponentsProcessFragment>
}>()

const hasNav = computed(() => props.data.steps.length > 1)

const activeStep = ref<number>(0)
const steps = ref< HTMLElement[]>([])
const ctx = useGsap.context(() => {})

const getFilename = (path: string): string => {
  const index = path.lastIndexOf('/')
  if (index === -1) {
    return path
  }
  return path.substring(index + 1)
}

const initScrolltrigger = () => {
  const mm = gsap.matchMedia()

  let sensorOffset = '146px'
  mm.add('(min-width: 1536px)', () => {
    sensorOffset = '220px'
  })
  mm.add('(min-width: 1920px)', () => {
    sensorOffset = '12%' // maybe we can find a solution to work with the rems on large screens
  })

  steps.value.forEach((step, index) => {
    ctx.add(() => {
      ScrollTrigger.create({
        trigger: step,
        start: 'top ' + sensorOffset,
        end: 'bottom ' + sensorOffset,
        markers: false,
        onToggle: (self) => {
          if (self.isActive) {
            activeStep.value = index
          }
        },
      })
    })
  })
}

onMounted(() => {
  initScrolltrigger()
})

onUnmounted(() => {
  ctx?.revert()
})

useHead({
  htmlAttrs: {
    style: 'scroll-padding-top: calc(var(--navbar-height) + 3.1875rem + 1rem);',
  },
})
</script>

<style>
.process-content {
  img {
    @apply mt-4 mb-6
  }
}
</style>
